import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { AttributeUsageEntry } from '../types';

interface UsageTabProps {
  usage: AttributeUsageEntry[];
}

/** Traceability chain: every Recipe/Item currently carrying this attribute, linking through
 * to the entity's own detail page. */
const UsageTab = ({ usage }: UsageTabProps) => {
  const { t } = useTranslation('attributesAllergens');
  const navigate = useNavigate();

  if (usage.length === 0) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('noUsage', 'This attribute is not currently used on any recipe or item.')}
        </Typography>
      </Paper>
    );
  }

  const navigateToEntity = (entry: AttributeUsageEntry) => {
    if (entry.entityType === 'Recipe') navigate(`/range/recipes/${entry.entityId}`);
    else if (entry.entityType === 'Menu') navigate(`/range/menus/${entry.entityId}`);
    else navigate(`/range-planning/items/${entry.entityId}`);
  };

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('entityType', 'Type')}</TableCell>
            <TableCell>{t('entityName', 'Name')}</TableCell>
            <TableCell>{t('attributeStatus', 'Status')}</TableCell>
            <TableCell>{t('sourceColumn', 'Source')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {usage.map((entry) => (
            <TableRow
              key={`${entry.entityType}-${entry.entityId}`}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigateToEntity(entry)}
            >
              <TableCell><Chip label={t(`entityType.${entry.entityType}`, entry.entityType)}
                size="small" /></TableCell>
              <TableCell>
                <Typography variant="textSM"
                  fontWeight={600}
                  color="primary.main">{entry.entityName}</Typography>
              </TableCell>
              <TableCell>{t(`attributeStatus.${entry.status}`, entry.status)}</TableCell>
              <TableCell>{t(`source.${entry.source}`, entry.source)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default UsageTab;
